import { C8Node, cnl } from "csi/typings";
import { ReactionStore } from "csi/utility/mobxUtility";
import { RadioOption } from "designSystem/components/radio/RadioGroup";
import { ILocaleProvider } from "designSystem/locale";
import { action, makeObservable, observable } from "mobx";

const ALLERGENS: string = "Allergens";
const INGREDIENTS: string = "Ingredients";
const NUTRIENTS: string = "Nutrients";
const UPDATE_OP: string = "Update";
const RESET_OP: string = "Reset";
const NOCALC_OP: string = "NoCalculation";
const CALCULATE_ACTIONS: string = "FoodLabelDataSheetRevision.Action.Calculate";

/** Return the Update, Reset, and NoCalculation options for a given ItemType */
function getOptionFromTemplate(itemType: string, locale: ILocaleProvider, userSettings: string[]): RadioOption<string>[] {
	const foodOperation = CALCULATE_ACTIONS + itemType;
	const oneItemType = itemType.slice(0, -1);
	const dataSheetSection = "LabelDataSheetSections:Food" + oneItemType;
	const itemTypeOptions: RadioOption<string>[] = [{
		name: locale.resource(`${foodOperation}_Update`), 
		invalid: false, 
		value: UPDATE_OP,
		helperText: locale.resource(`${foodOperation}_DescriptionForUpdate`),
		disabled: !userSettings.includes(dataSheetSection),
	}, {
		name: locale.resource(`${foodOperation}_Reset`),
		invalid: false,
		value: RESET_OP,
		helperText: locale.resource(`${foodOperation}_DescriptionForReset`),
		disabled: !userSettings.includes(dataSheetSection),
	}, {
		name: locale.resource(NOCALC_OP),
		invalid: false,
		value: NOCALC_OP,
		disabled: !userSettings.includes(dataSheetSection),
	}];
	return itemTypeOptions;
}

/** Return whether or not the current section is disabled */
function isSectionDisabled(itemType: string, dataSheetSections: string[]): boolean {
	return !dataSheetSections.includes("LabelDataSheetSections:Food" + itemType);
}

export class LabelCalculationStore extends ReactionStore {
	@observable protected _ingredientChecked: string;
	@observable protected _nutrientChecked: string;
	@observable protected _allergenChecked: string;
	@observable protected _dataSheetSections: string[];
	@observable protected _formWasSubmitted: boolean;
	protected documentCnl: cnl;

	readonly locale: ILocaleProvider;
	
	@action setDataSheetSections(sections: string[]) {
		this._dataSheetSections = sections;
	}

	@action setIngredientChecked(newValue: string) {
		this._ingredientChecked = newValue;
	}

	@action setNutrientChecked(newValue: string) {
		this._nutrientChecked = newValue;
	}

	@action setAllergenChecked(newValue: string) {
		this._allergenChecked = newValue;
	}

	/** Set the form submission status */
	@action setFormSubmitted = (submitted: boolean): void => {
		this._formWasSubmitted = submitted;
	};

	/** Return boolean that indicates which ingredient option is checked */
	get ingredientChecked(): string {
		return this._ingredientChecked;
	}

	/** Return boolean that indicates which nutrient option is checked */
	get nutrientChecked(): string {
		return this._nutrientChecked;
	}

	/** Return boolean that indicates which allergen option is checked */
	get allergenChecked(): string {
		return this._allergenChecked;
	}

	/** Return the datasheet sections that are present in the subtype settings */
	get dataSheetSections(): string[] {
		return this._dataSheetSections;
	}

	/** Return the Allergen Options (Update/Reset/No Calculation) for the form */
	get allergenOptions(): RadioOption<string>[] {
		return getOptionFromTemplate(ALLERGENS, this.locale, this.dataSheetSections);
	}

	/** Return the Nutrient Options (Update/Reset/No Calculation) for the form */
	get nutrientOptions(): RadioOption<string>[] {
		return getOptionFromTemplate(NUTRIENTS, this.locale, this.dataSheetSections);
	}

	/** Return the Ingredient options (Update/Reset/NoCalculation) for the form */
	get ingredientOptions(): object[] {
		return getOptionFromTemplate(INGREDIENTS, this.locale, this.dataSheetSections);
	}

	/** Return whether or the form can be submitted (at least one choice) */
	get canFormBeSubmitted(): boolean {
		return (this.ingredientChecked != NOCALC_OP ||
			this.allergenChecked != NOCALC_OP ||
			this.nutrientChecked != NOCALC_OP);
	}

	/** Return whether or the form was submitted */
	get formWasSubmitted(): boolean {
		return this._formWasSubmitted;
	}

	/** Returns whether or not the run button of the form is disabled */
	get isRunDisabled(): boolean {
		return !this.canFormBeSubmitted || this.formWasSubmitted;
	}
	
	/** Return whether or not the allergen section is disabled */
	get isAllergenSectionDisabled(): boolean{
		return isSectionDisabled(ALLERGENS, this.dataSheetSections);
	}

	/** Return whether or not the ingredient section is disabled */
	get isIngredientSectionDisabled(): boolean{
		return isSectionDisabled(INGREDIENTS, this.dataSheetSections);
	}

	/** Return whether or not the nutrient section is disabled */
	get isNutrientSectionDisabled(): boolean{
		return isSectionDisabled(NUTRIENTS, this.dataSheetSections);
	}

	constructor(document: C8Node, loc: ILocaleProvider, dataSheetSections: string[]) {
		super();
		makeObservable(this);
		this.documentCnl = document.$URL;
		this.locale = loc;
		this._dataSheetSections = dataSheetSections;
		this._ingredientChecked = NOCALC_OP;
		this._allergenChecked = NOCALC_OP;
		this._nutrientChecked = NOCALC_OP;
		this._formWasSubmitted = false;
	}
}